import { NgFor, NgIf } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild, output } from "@angular/core";
import { PopperHelper } from "@nts/std/utility";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";

let nextId = 0;

@Component({
    selector: 'nts-base-checkbox-text-box',
    templateUrl: './base-checkbox-text-box.component.html',
    standalone: true,
    imports: [
        NgxPopperjsModule,
        NgIf,
        NgFor
    ],
    styleUrls: ['./base-checkbox-text-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseCheckboxTextBoxComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() label = '';
    @Input() tabIndex = -1;
    @Input() isDisabled = false;
    @Input() isReadOnly = false;
    @Input() checkStatus = false;
    @Input() customClasses = '';
    @Input() errorList: string[] = [];
    @Input() showErrorTooltip = true;
    @Input() showTitleTooltip = true;
    @Input() showErrorBorder = true;
    @Input() labelText: string;
    @Input() listenClickOutside = false;
    @Input() defaultBorderColor: string = null;
    @Input() activeBorderColor: string = null;
    @Input() hoverBorderColor: string = null;

    /**
     * @deprecated use checkStatusChange
     */
    @Output() onChange = new EventEmitter<boolean>();

    readonly checkStatusChange = output<boolean>();
    readonly onClick = output<void>();
    readonly viewReady = output<void>();
    isViewReady = false;
    readonly onFinishEditing = output<void>();

    @ViewChild('checkboxBox', { static: true }) checkboxBox: ElementRef;
    @ViewChild('popperError', { static: false }) popperError: NgxPopperjsDirective;
    @ViewChild('popperInfo', { static: false }) popperInfo: NgxPopperjsDirective;
    @ViewChild('labelElement', { static: false }) labelElement: ElementRef;

    id = `chk-${nextId++}`;
    ngxPopperjsTriggers = NgxPopperjsTriggers;
    ngxPopperjsPlacements = NgxPopperjsPlacements;
    isActive = false;
    isHover = false;
    overrideBorderColor: string = null;

    private documentClickListener: any;
    private destroyed = false;

    constructor(
        public readonly el: ElementRef,
        private readonly renderer: Renderer2,
        private readonly zone: NgZone,
        private readonly cd: ChangeDetectorRef,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['defaultBorderColor'] || changes['activeBorderColor'] || changes['hoverBorderColor']) {
            this.handleOverridesColors();
        }
        if (changes['errorList']) {
            this.checkPopper();
        }
    }

   ngOnDestroy(): void {
        this.destroyed = true;
        this.unbindDocumentClickListener();
    }

    private unbindDocumentClickListener() {
      if (this.documentClickListener) {
        this.documentClickListener();
        this.documentClickListener = null;
      }
    }

    mouseEnter(e) {
        this.isHover = true;
        this.handleOverridesColors();
    }

    mouseLeave(e) {
        this.isHover = false;
        this.handleOverridesColors();
    }

    ngOnInit() {
        if (this.listenClickOutside) {
            this.bindDocumentClickListener();
        }
        this.handleOverridesColors();
    }

    ngAfterViewInit(): void {
        queueMicrotask(() => {
            if (!this.destroyed) {
                this.emitViewReady();
            }
        });
    }

    private emitViewReady(): void {
        this.isViewReady = true;
        this.viewReady.emit();
    }

    onFocus(e) {
        this.isActive = true;
        this.handleOverridesColors();
    }

    onBlur(e) {
        this.isActive = false;
        this.handleOverridesColors();
    }

    handleOverridesColors() {
        if (!this.defaultBorderColor || !this.activeBorderColor || !this.hoverBorderColor) {
            // devono essere impostate tutte e tre le variabili
            return;
        }

        this.overrideBorderColor = this.defaultBorderColor;
        if (this.isActive && !this.isDisabled) {
            this.overrideBorderColor = this.activeBorderColor;
        }
        if (this.isHover && !this.isDisabled) {
            this.overrideBorderColor = this.hoverBorderColor;
        }
    }

    keydownSpace(e: Event): void {
        if (!this.isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            this.toggleValue();
        }
    }

    click(e: Event): void {
        if (!this.isDisabled && !this.isReadOnly) {
            e.preventDefault();
            e.stopPropagation();
            this.toggleValue();
        } else {
            e.preventDefault();
            e.stopPropagation();
        }
        this.onClick.emit();
    }

    // for testing
    change(e: any): void {
        if (!this.isDisabled && !this.isReadOnly) {
            this.checkStatus = this.checkboxBox.nativeElement.checked;
            this.onChange.emit(this.checkStatus);
            this.checkStatusChange.emit(this.checkStatus);
            this.cd.detectChanges();
        }
    }

    toggleValue(): void {
        if (!this.isDisabled && !this.isReadOnly) {
            this.checkStatus = !this.checkStatus;
            this.checkboxBox.nativeElement.checked = this.checkStatus;
            this.onChange.emit(this.checkStatus);
            this.checkStatusChange.emit(this.checkStatus);
            this.cd.detectChanges();
        }
    }

    private isOutsideClicked(event: Event) {
        return !(this.el.nativeElement.isSameNode(event.target) || this.el.nativeElement.contains(event.target));
    }

    private bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.zone.runOutsideAngular(() => {
                const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

                this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event: Event) => {
                    if (this.isOutsideClicked(event)) {
                        this.zone.run(() => {
                            this.onFinishEditing.emit();

                            this.cd.markForCheck();
                        });
                    }

                });
            });
        }
    }

    private checkPopper() {
        if (this.popperError && (this.errorList == null || this.errorList.length === 0)) {
            PopperHelper.hide(this.popperError);
        }
        if (this.popperInfo && this.errorList?.length > 0) {
            PopperHelper.hide(this.popperInfo);
        }
    }
}
